import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import { ContactUsMode } from '../components/forms/ContactForm';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import StepCards from '../widgets/cards/StepCards';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import HowItWorksShowcase from '../widgets/showcases/HowItWorksShowcase';

// Dynamically import with SSR disabled
const LearnMoreShowcase = dynamic(() => import('../widgets/showcases/LearnMoreShowcase'), { ssr: false });
const SectionCoverParallax = dynamic(() => import('../components/interactive/SectionCoverParallax'), { ssr: false });

type DemoRequestPageStaticProps = {
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<DemoRequestPageStaticProps> = async (context) => {
  const articles = await StrapiApiClient.getRandomMostRecentArticles();
  const props: DemoRequestPageStaticProps = { articles };
  return withGlobalDefaultStaticProps(context, { props });
};

const DemoRequestPage: NextPageWithLayout<DemoRequestPageStaticProps> = (props) => {
  const { articles } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Demo);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        imageUrl={CDNAssets.getUrl('request-demo-page-hero')}
        title="Request a Demo"
        description="Transform employee support with Generative AI and offer employees the top-notch support they deserve."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrowTop={true}>
        <SectionCoverParallax
          overlaySection={<ContactUsWidget defaultMode={ContactUsMode.DemoRequest} showAllModes={false} />}
          underlaySection={
            <HowItWorksShowcase description="Fill in the form below and witness the magic of Gaspar AI in a 30-minute live demo tailored to you!" />
          }
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <StepCards
          records={[
            {
              title: 'Understand your needs',
              description: "Discuss your company's goals and current challenges.",
            },
            {
              title: 'Assess technical environment',
              description: 'Get a good understanding of your applications and systems.',
            },
            {
              title: 'Live overview of Gaspar AI',
              description: 'See our AI helpdesk working live to resolve your employee issues.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <LearnMoreShowcase articles={articles} />
      </PageSection>
    </>
  );
};
DemoRequestPage.displayName = 'DemoRequestPage';
export default DemoRequestPage;
